import {EMPTY_ATTACH_STATE} from "../../../components/files/editAttachState";
import {resolveThreadUserName} from "./taskProgressThreadUtils";

export const REWORK_FILE_TABLE_NAME = "rework_request";

export function reworkFileTable(reworkId) {
    return reworkId != null
        ? {tableName: REWORK_FILE_TABLE_NAME, joinKey: reworkId}
        : undefined;
}

/** @typedef {{ id?: number, name?: string, profileImage?: string }} ReworkUser */
/**
 * @typedef {Object} ReworkResponse
 * @property {number} [id]
 * @property {string} [workedAt]
 * @property {string} [deadLine]
 * @property {string} [content]
 * @property {string} [opinion]
 * @property {boolean} [pass]
 * @property {Array} [files]
 * @property {ReworkUser} [operator]
 * @property {ReworkUser} [reviewer]
 */

export function formatReworkDateTime(value) {
    if (!value) return undefined;
    const text = String(value);
    return text.includes("T") ? text.replace("T", " ").slice(0, 19) : text.slice(0, 19);
}

/** GET /rework/list 응답 → ReworkResponse[] */
export function normalizeReworkListResponse(payload) {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.content)) return payload.content;
    if (Array.isArray(payload.list)) return payload.list;
    if (Array.isArray(payload.items)) return payload.items;
    return [];
}

/** @typedef {'latest' | 'chrono'} ReworkFeedSortOrder */

/** rework 정렬용 시각(ms). workedAt 없/파싱 실패 시 id fallback */
export function parseReworkWorkedAtMs(entry) {
    if (entry?.workedAt) {
        const text = String(entry.workedAt).includes("T")
            ? String(entry.workedAt).replace("T", " ")
            : String(entry.workedAt);
        const ms = Date.parse(text);
        if (Number.isFinite(ms)) return ms;
    }

    const id = Number(entry?.id);
    return Number.isFinite(id) ? id : 0;
}

/** 최신순(latest) · 시간순(chrono) — 기준: workedAt, 없으면 id */
export function sortReworkListByTime(items, order = "latest") {
    const list = [...normalizeReworkListResponse(items)];
    list.sort((a, b) => {
        const diff = parseReworkWorkedAtMs(a) - parseReworkWorkedAtMs(b);
        return order === "chrono" ? diff : -diff;
    });
    return list;
}

/** 현재 작업 본문과 일치하는 이력 사이클 key (검수 입력 부착용) */
export function resolveAttachVerificationCycleKey(items, workBody, sortOrder = "latest") {
    if (!workBody?.trim()) return null;

    const list = sortReworkListByTime(items, sortOrder);
    if (!list.length) return null;

    const matching = list.filter((entry) => {
        const {work} = parseReworkResponseCycle(entry);
        return work?.body === workBody;
    });
    if (!matching.length) return null;

    const entry = sortOrder === "chrono" ? matching[matching.length - 1] : matching[0];
    const index = list.indexOf(entry);
    return entry.id ?? `rework-${index}`;
}

/** 검수(또는 재작업 확정)까지 끝난 사이클인지 */
export function hasCompletedVerification(entry) {
    const {verification} = parseReworkResponseCycle(entry);
    if (!verification) return false;
    if (verification.passed === true) return true;
    if (verification.body) return true;
    if (verification.deadlineChange) return true;
    return false;
}

/** rework 이력 최신 항목 */
export function resolveLatestReworkEntry(items) {
    const list = sortReworkListByTime(items, "latest");
    return list[0] ?? null;
}

/**
 * 재작업 등으로 새 작업 입력이 필요한지 — 이력 최신 사이클은 완료됐고, 저장만 된 미완료 초안이 없을 때
 */
export function needsFreshWorkInput(items) {
    if (resolveCurrentDraftWork(items)) return false;

    const latest = resolveLatestReworkEntry(items);
    if (!latest) return false;

    const {work} = parseReworkResponseCycle(latest);
    if (!work?.body) return false;

    return hasCompletedVerification(latest);
}

/**
 * 검수 단계에서 대상 작업 — 작업 본문이 있고 검수완료(pass) 또는 재작업 확정(pass=false) 전
 * @returns {{ reworkId?: number, content?: string, workTime?: string, files?: Array }|null}
 */
export function resolvePendingVerificationWork(items) {
    const list = sortReworkListByTime(items, "latest");
    if (!list.length) return null;

    const entry = list.find((item) => {
        const {work} = parseReworkResponseCycle(item);
        if (!work?.body) return false;
        if (isReworkPass(item.pass)) return false;
        if (isExplicitReworkPass(item)) return false;
        return true;
    });
    if (!entry) return null;

    const {work} = parseReworkResponseCycle(entry);
    return {
        reworkId: entry.id,
        content: work?.body,
        workTime: work?.time,
        files: work?.files ?? [],
    };
}

/**
 * 진행 단계에서 편집 중인 작업 — 문서 inProgressDescription 없을 때 rework 이력에서 복원
 * @returns {{ reworkId?: number, content?: string, workTime?: string }|null}
 */
export function resolveCurrentDraftWork(items) {
    const list = sortReworkListByTime(items, "latest");
    if (!list.length) return null;

    const entry = list.find((item) => {
        const {work, verification} = parseReworkResponseCycle(item);
        if (!work?.body) return false;
        return !verification?.passed && !verification?.body;
    });
    if (!entry) return null;

    const {work} = parseReworkResponseCycle(entry);
    return {
        reworkId: entry.id,
        content: work?.body,
        workTime: work?.time,
        files: work?.files ?? [],
    };
}

/** rework 응답 files → EditAttachPanel용 attachState */
export function buildWorkAttachStateFromFiles(files) {
    const existingDocs = normalizeEntryFiles({files}).map((file) => ({
        id: file.id,
        joinKey: file.joinKey,
        tableName: file.tableName,
        uploadName: file.uploadName,
        originalName: file.originalName || file.uploadName || file.name,
        path: file.path,
        type: file.type,
        ext: file.ext,
        size: file.size,
    }));

    return {
        ...EMPTY_ATTACH_STATE,
        existingDocs,
    };
}

export function findReworkEntry(reworkList, {reworkId, workBody} = {}) {
    const list = normalizeReworkListResponse(reworkList);
    if (!list.length) return null;

    if (reworkId != null) {
        const matched = list.find((entry) => entry.id === reworkId);
        if (matched) return matched;
    }

    const normalizedBody = workBody?.trim();
    if (!normalizedBody) return null;

    return list.find((entry) => entry.content?.trim() === normalizedBody) ?? null;
}

/** 진행 중 작업 수정 API용 reworkId */
export function resolveCurrentWorkReworkId(items, workBody) {
    const list = sortReworkListByTime(items, "latest");
    if (!list.length) return null;

    const normalizedBody = workBody?.trim();
    if (normalizedBody) {
        const matched = list.find((entry) => {
            const {work} = parseReworkResponseCycle(entry);
            return work?.body === normalizedBody;
        });
        if (matched?.id != null) return matched.id;
    }

    const pendingWork = list.find((entry) => {
        const {work, verification} = parseReworkResponseCycle(entry);
        if (!work) return false;
        return !verification?.passed && !verification?.body;
    });
    if (pendingWork?.id != null) return pendingWork.id;

    const latestWork = list.find((entry) => parseReworkResponseCycle(entry).work);
    return latestWork?.id ?? null;
}

export function isReworkPass(value) {
    return value === true || value === "true" || value === 1 || value === "1";
}

/** 재작업 요청(pass=false 등) 여부 */
export function isExplicitReworkPass(entry) {
    if (entry?.pass == null) return false;
    return !isReworkPass(entry.pass);
}

/** rework 응답 마감일 — 필드명 차이 흡수 */
export function resolveReworkDeadLine(entry) {
    if (!entry) return undefined;
    const raw = entry.deadLine ?? entry.deadline ?? entry.newDeadline ?? entry.afterEndDate;
    return formatReworkDateTime(raw);
}

export function resolveReworkBeforeDeadLine(entry) {
    if (!entry) return undefined;
    const raw = entry.beforeEndDate ?? entry.beforeDeadLine ?? entry.beforeDeadline;
    return formatReworkDateTime(raw);
}

function normalizeEntryFiles(entry) {
    const raw = entry?.files ?? entry?.fileList ?? entry?.attachments;
    return Array.isArray(raw) ? raw.filter(Boolean) : [];
}

/**
 * @param {ReworkResponse} entry
 * @returns {{ work: object|null, verification: object|null }}
 */
export function parseReworkResponseCycle(entry) {
    if (!entry) return {work: null, verification: null};

    const workedAt = formatReworkDateTime(entry.workedAt);
    const resolvedDeadLine = resolveReworkDeadLine(entry);
    const resolvedBeforeDeadLine = resolveReworkBeforeDeadLine(entry);
    const workContent = entry.content?.trim();
    const opinion = entry.opinion?.trim();
    const passed = isReworkPass(entry.pass);
    const isRework = isExplicitReworkPass(entry);

    let work = null;
    if (workContent || entry.workedAt || entry.operator) {
        work = {
            user: entry.operator,
            name: resolveThreadUserName(entry.operator),
            time: workedAt,
            body: workContent || undefined,
            deadLine: resolvedDeadLine && (workContent || !isRework) ? resolvedDeadLine : undefined,
            files: normalizeEntryFiles(entry),
            fileJoinKey: entry.id,
        };
    }

    const pendingWorkReview = Boolean(workContent && !passed && !opinion);

    let verification = null;
    if (!pendingWorkReview && (passed || opinion || entry.reviewer || (isRework && resolvedDeadLine))) {
        verification = {
            user: entry.reviewer,
            name: resolveThreadUserName(entry.reviewer),
            time: workedAt,
            passed,
            body: opinion || undefined,
            deadlineChange: isRework && resolvedDeadLine
                ? {
                    before: resolvedBeforeDeadLine,
                    after: resolvedDeadLine,
                }
                : null,
        };
    }

    return {work, verification};
}
